import React, { useState, useRef, useEffect } from 'react';
import '../styles/auth-theme.css';
import { useAuth } from '../auth/AuthContext.js';

type Screen = 'email' | 'method' | 'code' | 'passkeyEnrollment';

const CODE_LENGTH = 6;

function BrandHeader(): React.JSX.Element {
  return (
    <div className="auth-brand">
      <div className="auth-wordmark">
        <span className="w-syn">Syn</span>
        <span className="w-phant">phant</span>
      </div>
    </div>
  );
}

export function AuthPage(): React.JSX.Element {
  const { initiateAuth, signInWithPasskey, sendVerificationCode, verifyCode, registerPasskey, skipPasskey, cliLoginParams, error, clearError } = useAuth();

  const [screen, setScreen] = useState<Screen>('email');
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const codeRefs = useRef<Array<HTMLInputElement | null>>(Array(CODE_LENGTH).fill(null));

  // Clear resend message when screen changes
  useEffect(() => {
    setResendMessage(null);
  }, [screen]);

  // Autofocus first code box when entering code screen
  useEffect(() => {
    if (screen === 'code') {
      codeRefs.current[0]?.focus();
    }
  }, [screen]);

  async function handleContinue(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    clearError();
    setIsSubmitting(true);
    try {
      await initiateAuth(emailInput);
      setScreen('method');
    } catch {
      // AuthContext sets error state; nothing else to do here
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasskey(): Promise<void> {
    clearError();
    setIsSubmitting(true);
    try {
      await signInWithPasskey();
      // AuthContext navigates away on success
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSendCode(): Promise<void> {
    clearError();
    setIsSubmitting(true);
    try {
      await sendVerificationCode();
      setCodeDigits(Array(CODE_LENGTH).fill(''));
      setScreen('code');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyCode(code: string): Promise<void> {
    clearError();
    setIsSubmitting(true);
    try {
      const result = await verifyCode(code);
      if (cliLoginParams || result.hasPasskey) {
        skipPasskey();
      } else {
        setScreen('passkeyEnrollment');
      }
    } catch {
      // AuthContext sets error; clear code boxes so user can re-enter
      setCodeDigits(Array(CODE_LENGTH).fill(''));
      codeRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    await handleVerifyCode(codeDigits.join(''));
  }

  async function handleResend(): Promise<void> {
    clearError();
    setResendMessage(null);
    try {
      await sendVerificationCode();
      setResendMessage('Code sent!');
    } catch {
      // AuthContext will set error prop
    }
  }

  async function handleSetupPasskey(): Promise<void> {
    clearError();
    setIsSubmitting(true);
    try {
      await registerPasskey();
      // Show success — skipPasskey navigates away
      skipPasskey();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCodeInput(index: number, value: string): void {
    clearError();

    // Handle paste of full code
    if (value.length === CODE_LENGTH && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      setCodeDigits(digits);
      codeRefs.current[CODE_LENGTH - 1]?.focus();
      void handleVerifyCode(digits.join(''));
      return;
    }

    // Accept only single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...codeDigits];
    next[index] = digit;
    setCodeDigits(next);

    if (digit && index < CODE_LENGTH - 1) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (digit && next.every(d => d !== '')) {
      void handleVerifyCode(next.join(''));
    }
  }

  function handleCodeKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Backspace') {
      if (codeDigits[index]) {
        const next = [...codeDigits];
        next[index] = '';
        setCodeDigits(next);
      } else if (index > 0) {
        codeRefs.current[index - 1]?.focus();
      }
    }
  }

  function handleCodePaste(e: React.ClipboardEvent<HTMLInputElement>): void {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (pasted.length === CODE_LENGTH) {
      e.preventDefault();
      const digits = pasted.split('');
      setCodeDigits(digits);
      codeRefs.current[CODE_LENGTH - 1]?.focus();
      void handleVerifyCode(pasted);
    }
  }

  return (
    <div className="auth-page">
      <img className="auth-watermark" src="/synphant-badge.png" alt="" />
      <div className="auth-card">
        <BrandHeader />

        {screen === 'email' && (
          <form onSubmit={(e) => void handleContinue(e)}>
            <h1 className="auth-title">Sign In</h1>
            <p className="auth-subtitle">Enter your email to continue</p>

            <input
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => {
                clearError();
                setEmailInput(e.target.value);
              }}
              required
              autoFocus
              autoComplete="email"
              disabled={isSubmitting}
            />

            {error && <p className="auth-error">{error}</p>}

            <button
              type="submit"
              className="auth-btn-primary"
              disabled={isSubmitting || !emailInput}
            >
              {isSubmitting ? 'Checking...' : 'Continue'}
            </button>

            <p className="auth-terms">
              By continuing you accept the{' '}
              <a href="/terms.pdf" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </a>
            </p>
          </form>
        )}

        {screen === 'method' && (
          <div>
            <h1 className="auth-title">Choose Method</h1>
            <p className="auth-subtitle">How would you like to sign in?</p>

            {error && <p className="auth-error">{error}</p>}

            <button
              type="button"
              className="auth-btn-primary"
              onClick={() => void handlePasskey()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Use Passkey'}
            </button>

            <button
              type="button"
              className="auth-btn-outline"
              onClick={() => void handleSendCode()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        )}

        {screen === 'code' && (
          <form onSubmit={(e) => void handleVerify(e)}>
            <h1 className="auth-title">Enter Code</h1>
            <p className="auth-subtitle">
              We sent a code to <strong>{emailInput}</strong>
            </p>

            <div className="auth-code-container">
              {codeDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { codeRefs.current[i] = el; }}
                  className="auth-code-box"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleCodeInput(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onPaste={handleCodePaste}
                  disabled={isSubmitting}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            {error && <p className="auth-error">{error}</p>}
            {resendMessage && <p className="auth-success">{resendMessage}</p>}

            <button
              type="submit"
              className="auth-btn-primary"
              disabled={isSubmitting || codeDigits.join('').length < CODE_LENGTH}
            >
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </button>

            <p className="auth-terms">
              Didn&apos;t receive it?{' '}
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); void handleResend(); }}
              >
                Resend code
              </a>
            </p>
          </form>
        )}

        {screen === 'passkeyEnrollment' && (
          <div>
            <div className="auth-checkmark" aria-hidden="true">✅</div>
            <h1 className="auth-title">You&apos;re Verified!</h1>
            <p className="auth-subtitle">
              Set up a passkey for faster sign-in next time?
            </p>

            {error && <p className="auth-error">{error}</p>}

            <button
              type="button"
              className="auth-btn-primary"
              onClick={() => void handleSetupPasskey()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Setting up...' : 'Set Up Passkey'}
            </button>

            <button
              type="button"
              className="auth-btn-outline"
              onClick={skipPasskey}
              disabled={isSubmitting}
            >
              Just Log In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
